// ============================================================================
//  VOICE ANNOUNCER - Text-to-Speech for Poker Game

// ============================================================================
// Uses Web Speech API (built-in, no copyright issues)
// Provides immersive poker experience with voice callouts
//  UPDATED: Added dealer tip announcements

//  UPDATED: Added male/female voice selection for dealer

// ============================================================================

type AnnouncementType = 
  | 'action'      // Player actions (raise, call, fold, etc.)
  | 'winner'      // Win announcements
  | 'turn'        // Your turn notification
  | 'cards'       // Card dealing announcements
  | 'dramatic'    // Big moments (all-in, big pot, etc.)
  | 'info'        // General info
  | 'tip';        //  Dealer tip announcements


type VoiceGender = 'male' | 'female';

interface VoiceSettings {
  rate: number;      // Speed: 0.1 to 10 (1 is normal)
  pitch: number;     // Pitch: 0 to 2 (1 is normal)
  volume: number;    // Volume: 0 to 1
}

// Voice presets for different announcement types
const VOICE_PRESETS: Record<AnnouncementType, VoiceSettings> = {
  action: { rate: 1.1, pitch: 1.0, volume: 0.9 },
  winner: { rate: 0.9, pitch: 1.2, volume: 1.0 },
  turn: { rate: 1.0, pitch: 1.0, volume: 1.0 },
  cards: { rate: 1.0, pitch: 0.9, volume: 0.8 },
  dramatic: { rate: 0.8, pitch: 0.8, volume: 1.0 },
  info: { rate: 1.0, pitch: 1.0, volume: 0.7 },
  tip: { rate: 0.95, pitch: 1.1, volume: 0.9 }, // Friendly, appreciative tone
};

// Fun phrases for different scenarios
const WIN_PHRASES = [
  "Winner winner chicken dinner!",
  "And the pot goes to {name}!",
  "Congratulations {name}, you've won!",
  "{name} takes it down!",
  "Ship it to {name}!",
  "{name} rakes in the chips!",
];

// Phrases when winner is announced with their hand (showdown)
const WIN_PHRASES_WITH_HAND = [
  "Winner winner chicken dinner! {name} wins with {hand}!",
  "{name} takes it down with {hand}!",
  "And {name} wins with {hand}!",
  "{name} shows {hand} and takes the pot!",
  "Congratulations {name}! {hand} wins it!",
];

// Phrases when everyone else folded
const WIN_PHRASES_FOLD = [
  "{name} takes it down! Everyone else folded.",
  "The rest fold! {name} wins the pot!",
  "{name} wins uncontested!",
];

// Split pot phrases
const SPLIT_POT_PHRASES = [
  "Split pot! You chop it up!",
  "It's a split! You share the pot!",
  "Chop chop! Split pot!",
  "You split the pot!",
];

const SPLIT_POT_PHRASES_LOST = [
  "Split pot between {names}.",
  "{names} chop it up.",
  "The pot is split between {names}.",
];

const LOSE_PHRASES = [
  "Better luck next time.",
  "The cards weren't with you this time.",
  "Stay in the game, your moment will come.",
];

const ALL_IN_PHRASES = [
  "{name} is all in!",
  "All in from {name}! This is it!",
  "{name} pushes all in!",
  "Bold move! {name} goes all in!",
];

const BIG_RAISE_PHRASES = [
  "{name} fires a big raise!",
  "{name} puts on the pressure!",
  "A power move from {name}!",
];

const FOLD_PHRASES = [
  "{name} folds.",
  "{name} mucks their hand.",
  "{name} is out of this one.",
];

const CHECK_PHRASES = [
  "{name} checks.",
  "Check from {name}.",
];

const CALL_PHRASES = [
  "{name} calls.",
  "{name} makes the call.",
  "Call from {name}.",
];

// Dealer tip phrases
const TIP_PHRASES_SELF = [
  "Thanks for the tip!",
  "Much appreciated!",
  "Thank you, generous player!",
  "The dealer says thank you!",
  "What a kind gesture!",
];

const TIP_PHRASES_OTHER = [
  "{name} tips the dealer!",
  "{name} shows appreciation to the dealer!",
  "A tip from {name}!",
  "{name} is feeling generous!",
  "Nice tip from {name}!",
];

const TIP_PHRASES_BIG = [
  "Wow! {name} tips big!",
  "A generous tip from {name}!",
  "{name} with a royal tip!",
  "Big money tip from {name}!",
];

class VoiceAnnouncer {
  private enabled: boolean = true;
  private synth: SpeechSynthesis | null = null;
  private preferredVoice: SpeechSynthesisVoice | null = null;
  private voiceGender: VoiceGender = 'male';
  private voicesLoaded: boolean = false;
  private lastAnnouncement: string = '';
  private lastAnnouncementTime: number = 0;
  private minTimeBetweenSame: number = 3000; // Don't repeat same announcement within 3s

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
      this.loadVoices();
      
      // Voices might load async
      if (this.synth.onvoiceschanged !== undefined) {
        this.synth.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  private loadVoices(): void {
    if (!this.synth) return;
    
    const voices = this.synth.getVoices();
    if (voices.length === 0) return;
    
    this.voicesLoaded = true;
    this.selectVoiceByGender(this.voiceGender);
  }

  private selectVoiceByGender(gender: VoiceGender): void {
    if (!this.synth) return;
    
    const voices = this.synth.getVoices();
    
    // Preferred voices by gender
    const maleVoiceNames = [
      'Google US English',
      'Google UK English Male',
      'Microsoft David',
      'Microsoft Mark',
      'Alex',
      'Daniel',
      'Fred',
      'Tom',
    ];
    
    const femaleVoiceNames = [
      'Google US English Female',
      'Microsoft Zira',
      'Microsoft Hazel',
      'Samantha',
      'Victoria',
      'Karen',
      'Moira',
      'Fiona',
      'Tessa',
      'Veena',
    ];
    
    const preferredNames = gender === 'female' ? femaleVoiceNames : maleVoiceNames;
    
    // Try to find a preferred voice
    for (const name of preferredNames) {
      const voice = voices.find(v => v.name.toLowerCase().includes(name.toLowerCase()));
      if (voice) {
        this.preferredVoice = voice;
        console.log(`🎙️ ${gender} voice selected: ${voice.name}`);
        return;
      }
    }
    
    // Fallback: try to find any voice that sounds like the gender
    const genderHints = gender === 'female' 
      ? ['female', 'woman', 'girl', 'zira', 'hazel', 'samantha', 'victoria', 'karen', 'susan', 'linda']
      : ['male', 'man', 'david', 'mark', 'alex', 'daniel', 'james', 'tom', 'fred'];
    
    for (const hint of genderHints) {
      const voice = voices.find(v => v.name.toLowerCase().includes(hint));
      if (voice) {
        this.preferredVoice = voice;
        console.log(`🎙️ ${gender} voice fallback: ${voice.name}`);
        return;
      }
    }
    
    // Final fallback to first English voice
    this.preferredVoice = voices.find(v => v.lang.startsWith('en')) || voices[0];
    if (this.preferredVoice) {
      console.log(`🎙️ Voice generic fallback: ${this.preferredVoice.name}`);
    }
  }

  /**
   * Set voice gender based on dealer type
   * @param gender - 'male' or 'female'
   */
  public setVoiceGender(gender: VoiceGender): void {
    this.voiceGender = gender;
    if (this.voicesLoaded) {
      this.selectVoiceByGender(gender);
    }
    console.log(`🎙️ Voice gender set to: ${gender}`);
  }

  /**
   * Set voice gender from dealer image URL
   * Automatically detects 'female' in URL
   */
  public setVoiceFromDealerImage(dealerImageUrl: string): void {
    const isFemale = dealerImageUrl.toLowerCase().includes('female');
    this.setVoiceGender(isFemale ? 'female' : 'male');
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled && this.synth) {
      this.synth.cancel(); // Stop any current speech
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  private getRandomPhrase(phrases: string[], name?: string): string {
    const phrase = phrases[Math.floor(Math.random() * phrases.length)];
    return name ? phrase.replace('{name}', name) : phrase;
  }

  private speak(text: string, type: AnnouncementType = 'info'): void {
    if (!this.enabled || !this.synth) return;

    // Prevent rapid repeat of same announcement
    const now = Date.now();
    if (text === this.lastAnnouncement && now - this.lastAnnouncementTime < this.minTimeBetweenSame) {
      return;
    }
    this.lastAnnouncement = text;
    this.lastAnnouncementTime = now;

    // Cancel any current speech
    this.synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const settings = VOICE_PRESETS[type];
    
    utterance.rate = settings.rate;
    utterance.pitch = settings.pitch;
    utterance.volume = settings.volume;
    
    if (this.preferredVoice) {
      utterance.voice = this.preferredVoice;
    }

    this.synth.speak(utterance);
  }

  // ============================================================================
  // PUBLIC ANNOUNCEMENT METHODS
  // ============================================================================

  /**
   * Announce when it's the player's turn
   */
  public announceYourTurn(): void {
    const phrases = [
      "Your turn.",
      "It's on you.",
      "Action's on you.",
      "Your move.",
    ];
    this.speak(this.getRandomPhrase(phrases), 'turn');
  }

  /**
   * Announce a player's raise
   */
  public announceRaise(playerName: string, amount?: number): void {
    let phrase: string;
    if (amount && amount > 50) {
      phrase = this.getRandomPhrase(BIG_RAISE_PHRASES, playerName);
    } else {
      const phrases = [
        `${playerName} raises.`,
        `Raise from ${playerName}.`,
        `${playerName} bumps it up.`,
      ];
      phrase = this.getRandomPhrase(phrases);
    }
    this.speak(phrase, 'action');
  }

  /**
   * Announce all-in
   */
  public announceAllIn(playerName: string): void {
    this.speak(this.getRandomPhrase(ALL_IN_PHRASES, playerName), 'dramatic');
  }

  /**
   * Announce a fold
   */
  public announceFold(playerName: string): void {
    this.speak(this.getRandomPhrase(FOLD_PHRASES, playerName), 'action');
  }

  /**
   * Announce a check
   */
  public announceCheck(playerName: string): void {
    this.speak(this.getRandomPhrase(CHECK_PHRASES, playerName), 'action');
  }

  /**
   * Announce a call
   */
  public announceCall(playerName: string): void {
    this.speak(this.getRandomPhrase(CALL_PHRASES, playerName), 'action');
  }

  /**
   * Announce the winner with hand description
   */
  public announceWinner(playerName: string, isYou: boolean, handDescription?: string, isFoldWin?: boolean): void {
    if (isYou) {
      let phrase: string;
      
      if (isFoldWin) {
        phrase = this.getRandomPhrase(WIN_PHRASES_FOLD, playerName);
      } else if (handDescription) {
        phrase = this.getRandomPhrase(WIN_PHRASES_WITH_HAND, playerName)
          .replace('{hand}', handDescription);
      } else {
        phrase = this.getRandomPhrase(WIN_PHRASES, playerName);
      }
      
      this.speak(phrase, 'winner');
    }
  }

  /**
   * Announce split pot
   */
  public announceSplitPot(isYou: boolean, winnerNames?: string[]): void {
    if (isYou) {
      this.speak(this.getRandomPhrase(SPLIT_POT_PHRASES), 'winner');
    } else if (winnerNames && winnerNames.length > 0) {
      const namesStr = winnerNames.length === 2 
        ? `${winnerNames[0]} and ${winnerNames[1]}`
        : winnerNames.slice(0, -1).join(', ') + ', and ' + winnerNames[winnerNames.length - 1];
      const phrase = this.getRandomPhrase(SPLIT_POT_PHRASES_LOST).replace('{names}', namesStr);
      this.speak(phrase, 'info');
    }
  }

  /**
   * Announce when you lose
   */
  public announceLoss(): void {
    this.speak(this.getRandomPhrase(LOSE_PHRASES), 'info');
  }

  /**
   * Announce community cards
   */
  public announceFlop(): void {
    const phrases = [
      "Here comes the flop.",
      "The flop is out.",
      "Flop.",
    ];
    this.speak(this.getRandomPhrase(phrases), 'cards');
  }

  public announceTurn(): void {
    const phrases = [
      "The turn.",
      "Fourth street.",
      "Here's the turn card.",
    ];
    this.speak(this.getRandomPhrase(phrases), 'cards');
  }

  public announceRiver(): void {
    const phrases = [
      "The river.",
      "Fifth street.",
      "Here comes the river.",
      "Final card.",
    ];
    this.speak(this.getRandomPhrase(phrases), 'cards');
  }

  public announceShowdown(): void {
    const phrases = [
      "Showdown!",
      "Let's see those cards!",
      "Time to show 'em.",
    ];
    this.speak(this.getRandomPhrase(phrases), 'dramatic');
  }

  /**
   * Announce game starting
   */
  public announceGameStart(): void {
    const phrases = [
      "Shuffle up and deal!",
      "Let's play some poker!",
      "Cards are in the air!",
      "Good luck everyone!",
    ];
    this.speak(this.getRandomPhrase(phrases), 'dramatic');
  }

  /**
   * Announce new hand
   */
  public announceNewHand(handNumber?: number): void {
    if (handNumber && handNumber % 10 === 0) {
      this.speak(`Hand number ${handNumber}. Keep it going!`, 'info');
    } else {
      const phrases = [
        "New hand.",
        "Next hand coming up.",
        "Dealing.",
      ];
      this.speak(this.getRandomPhrase(phrases), 'cards');
    }
  }

  /**
   * Announce pot size for big pots
   */
  public announceBigPot(potSize: number): void {
    if (potSize >= 100) {
      this.speak(`Big pot! ${potSize} in the middle.`, 'dramatic');
    }
  }

  /**
   * Announce player joining
   */
  public announcePlayerJoined(playerName: string): void {
    this.speak(`${playerName} has joined the table.`, 'info');
  }

  /**
   * Announce player leaving
   */
  public announcePlayerLeft(playerName: string): void {
    this.speak(`${playerName} has left the table.`, 'info');
  }

  /**
   * Announce blinds
   */
  public announceBlinds(smallBlind: number, bigBlind: number): void {
    this.speak(`Blinds are ${smallBlind} and ${bigBlind}.`, 'info');
  }

  // ============================================================================
  // DEALER TIP ANNOUNCEMENTS
  // ============================================================================

  /**
   * Announce dealer tip
   */
  public announceDealerTip(
    tipperName: string, 
    amount: number, 
    currency: 'pkcash' | 'playcoins',
    isYou: boolean = true
  ): void {
    const isBigTip = currency === 'pkcash' ? amount >= 2 : amount >= 1000;

    if (isYou) {
      this.speak(this.getRandomPhrase(TIP_PHRASES_SELF), 'tip');
    } else if (isBigTip) {
      this.speak(this.getRandomPhrase(TIP_PHRASES_BIG, tipperName), 'tip');
    } else {
      this.speak(this.getRandomPhrase(TIP_PHRASES_OTHER, tipperName), 'tip');
    }
  }

  /**
   * Announce thank you for tip
   */
  public announceTipThankYou(): void {
    const phrases = [
      "Thank you!",
      "Much appreciated!",
      "Thanks for the tip!",
      "You're the best!",
    ];
    this.speak(this.getRandomPhrase(phrases), 'tip');
  }

  // ============================================================================
  // SPECIAL RULES ANNOUNCEMENTS
  // ============================================================================

  /**
   * Announce bomb pot
   */
  public announceBombPot(ante: number): void {
    const phrases = [
      `Bomb pot! Everyone antes ${ante}!`,
      `It's a bomb pot! ${ante} ante from everyone!`,
      `Boom! Bomb pot time! ${ante} to play!`,
    ];
    this.speak(this.getRandomPhrase(phrases), 'dramatic');
  }

  /**
   * Announce 7-2 bounty win
   */
  public announce72Bounty(winnerName: string, bountyAmount: number): void {
    const phrases = [
      `${winnerName} wins with seven-two! ${bountyAmount} bounty collected!`,
      `Seven-two game! ${winnerName} takes the ${bountyAmount} bounty!`,
      `The worst hand wins! ${winnerName} gets ${bountyAmount}!`,
    ];
    this.speak(this.getRandomPhrase(phrases), 'dramatic');
  }

  /**
   * Announce straddle
   */
  public announceStraddle(playerName: string, amount: number): void {
    this.speak(`${playerName} straddles for ${amount}!`, 'action');
  }

  /**
   * Announce run it twice
   */
  public announceRunItTwice(): void {
    const phrases = [
      "Running it twice!",
      "Let's run it twice!",
      "Two boards coming!",
    ];
    this.speak(this.getRandomPhrase(phrases), 'dramatic');
  }

  // ============================================================================
  // GENERIC METHODS
  // ============================================================================

  /**
   * Custom announcement
   */
  public say(text: string, type: AnnouncementType = 'info'): void {
    this.speak(text, type);
  }

  /**
   * Stop current speech
   */
  public stop(): void {
    if (this.synth) {
      this.synth.cancel();
    }
  }

  /**
   * Check if currently speaking
   */
  public isSpeaking(): boolean {
    return this.synth?.speaking || false;
  }
}

// Export singleton instance
const voiceAnnouncer = new VoiceAnnouncer();
export default voiceAnnouncer;